import React from "react";
import * as S from "./style"
import { ramps, hexToRGBA } from "../../../common/theme/brandTokens";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditIcon from '@mui/icons-material/Edit';
import { BsLayoutSidebar } from 'react-icons/bs';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

import Logo from "../../../assets/logo-notext.png";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../common/constant/routes";
import { Stack } from "@mui/system";
import { lastUpdated } from "./helpers";
import { Conversation, SidebarProps } from "./types";
import ProfileSection from "../../../components/ProfileSection";
import { useLanguage } from "../../../contexts/LanguageContext";

function Sidebar(props: SidebarProps) {
  const { t } = useLanguage();

  const navigate = useNavigate();
  const conversationId = props.selectedId;
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuConversationId, setMenuConversationId] = React.useState<string | null>(null);
  const handleSelectConversation = (id: string, shortCode: string) =>
    props.onSelect(id, shortCode);
  const handleNewConversation = () => props.onNewConversation();
  const setIsHistoryOpen = (value: boolean) => (value ? props.onOpen() : props.onClose());
  const handleLogoClick = () => {
    props.onLogoClick?.();
    navigate(ROUTES.DASHBOARD);
  };

  const handleLogoClickWithPrevent = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent bubbling to parent IconButton
    handleLogoClick();
  };

  const formatLastUpdated = React.useCallback((value: string) => lastUpdated(value, t), [t]);
  const isMenuOpen = Boolean(menuAnchorEl);
  const [view, setView] = React.useState<"active" | "archived">("active");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [editingConversationId, setEditingConversationId] = React.useState<string | null>(null);
  const [editingText, setEditingText] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Auto-select text when entering edit mode
  React.useEffect(() => {
    if (editingConversationId && inputRef.current) {
      inputRef.current.select();
    }
  }, [editingConversationId]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuConversationId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuConversationId(null);
  };

  const handleArchive = () => {
    if (menuConversationId) {
      const target = props.conversations.find((item) => item.uuid === menuConversationId);
      const nextStatus = target?.status === "archived" ? "active" : "archived";
      props.onArchiveConversation?.(menuConversationId, nextStatus);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuConversationId) {
      props.onDeleteConversation?.(menuConversationId);
    }
    handleMenuClose();
  };

  const handleRename = () => {
    if (menuConversationId) {
      const conversation = props.conversations.find((item) => item.uuid === menuConversationId);
      setEditingConversationId(menuConversationId);
      setEditingText(conversation?.text || "");
    }
    handleMenuClose();
  };

  const handleSaveRename = async () => {
    if (!editingConversationId || !props.onUpdateConversationSummary) return;
    
    try {
      await props.onUpdateConversationSummary(editingConversationId, editingText);
      setEditingConversationId(null);
      setEditingText("");
    } catch (error) {
      console.error("Failed to update summary:", error);
    }
  };

  const handleCancelRename = () => {
    setEditingConversationId(null);
    setEditingText("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveRename();
    } else if (event.key === "Escape") {
      handleCancelRename();
    }
  };

  const hasArchived = React.useMemo(
    () => props.conversations.some((item) => item.status === "archived"),
    [props.conversations]
  );

  const visibleConversations = React.useMemo(() => {
    return props.conversations.filter((item) =>
      view === "archived" ? item.status === "archived" : item.status !== "archived"
    );
  }, [props.conversations, view]);

  const groupedConversations = React.useMemo(() => {
    const sorted = [...visibleConversations].sort(
      (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
    );
    return sorted.reduce<Record<string, Conversation[]>>((acc, item) => {
      const label = formatLastUpdated(item.lastUpdated) || "Unknown";
      acc[label] = acc[label] ? [...acc[label], item] : [item];
      return acc;
    }, {});
  }, [visibleConversations, formatLastUpdated]);

  const searchFilteredConversations = React.useMemo(() => {
    if (!searchQuery) return visibleConversations;
    const q = searchQuery.toLowerCase();
    return visibleConversations.filter((item) =>
      (item.text || "").toLowerCase().includes(q)
    );
  }, [visibleConversations, searchQuery]);

  const searchGroupedConversations = React.useMemo(() => {
    const sorted = [...searchFilteredConversations].sort(
      (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
    );
    return sorted.reduce<Record<string, Conversation[]>>((acc, item) => {
      const label = formatLastUpdated(item.lastUpdated) || "Unknown";
      acc[label] = acc[label] ? [...acc[label], item] : [item];
      return acc;
    }, {});
  }, [searchFilteredConversations, formatLastUpdated]);

  return (
    <>
      {!props.open && (
        <S.StyledBox>
          <IconButton
            onClick={() => setIsHistoryOpen(true)}
            aria-label="Show history"
            sx={{
              "&:hover": { backgroundColor: 'transparent' },
              "&:hover .sidebar-toggle-icon": { transform: 'rotate(180deg)' },
            }}
          >
            <span
              className="sidebar-toggle-icon"
              style={{ display: 'flex', transition: 'transform 0.5s ease' }}
            >
              {React.createElement(BsLayoutSidebar as React.ElementType, { size: 18 })}
            </span>
          </IconButton>
        </S.StyledBox>
      )}
      <S.StyledGrid size={{ xs: 4 }}>
        <Slide
          in={props.open}
          direction="right"
          mountOnEnter
          unmountOnExit
          appear={false}
          timeout={{ enter: 300, exit: 240 }}
        >
          <S.StyledBoxPaperContainer>
            <S.StyledPaper elevation={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component="img"
                    src={Logo}
                    alt="Logo"
                    sx={{ width: 50, cursor: "pointer" }}
                    onClick={handleLogoClickWithPrevent}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setIsHistoryOpen(false)}
                  aria-label="Hide history"
                  sx={{ p: 1 }}
                >
                  <span style={{ display: 'flex' }}>{React.createElement(BsLayoutSidebar as React.ElementType, { size: 18, style: { transform: 'rotate(180deg)' } })}</span>
                </IconButton>
              </Stack>
              <Stack mt={1}>
                <S.StyledButton
                  variant="text"
                  onClick={handleLogoClick}
                  disableElevation
                  fullWidth
                  startIcon={<ArrowBackRoundedIcon fontSize="large" />}
                >
                  <Typography variant="body2">{t('sidebar.backToPatients')}</Typography>
                </S.StyledButton>
                {!props.readOnly && (
                  <S.StyledButton
                    variant="text"
                    onClick={handleNewConversation}
                    disableElevation
                    fullWidth
                    startIcon={<EditNoteIcon fontSize="large" />}
                  >
                    <Typography variant="body2">{t('sidebar.newChat')}</Typography>
                  </S.StyledButton>
                )}
                <S.StyledButton
                  variant="text"
                  onClick={() => setSearchOpen(true)}
                  disableElevation
                  fullWidth
                  startIcon={<SearchRoundedIcon fontSize="large" />}
                >
                  <Typography variant="body2">{t('sidebar.searchChats')}</Typography>
                </S.StyledButton>
              </Stack>
              <Divider />
              <S.SectionHeader>
                <S.SectionToggle
                  type="button"
                  onClick={() =>
                    hasArchived || view === "archived"
                      ? setView((prev) => (prev === "archived" ? "active" : "archived"))
                      : null
                  }
                  aria-label={`Switch to ${view === "archived" ? "chats" : "archive"}`}
                  disabled={!hasArchived && view === "active"}
                >
                  <Typography variant="body2">{view === "archived" ? t('sidebar.yourArchives') : t('sidebar.yourChats')}</Typography>
                  {hasArchived && (
                    <KeyboardArrowDownRoundedIcon
                      fontSize="small"
                      sx={{ transform: view === "archived" ? "rotate(180deg)" : "none" }}
                    />
                  )}
                </S.SectionToggle>
              </S.SectionHeader>
              <List
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { display: "none" },
                  py: 0,
                  pb: 2,
                  minHeight: 0,
                }}
              >
                {visibleConversations.length === 0 && (
                  <Typography variant="body2" color="text.secondary" p={3} align="center">
                    {view === "archived"
                      ? t('sidebar.noArchivedYet')
                      : t('sidebar.noChatsYet')}
                  </Typography>
                )}
                {Object.entries(groupedConversations).map(([label, items]) => (
                  <Box key={label} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1, pb: 0.5, display: "block" }}>
                      {label}
                    </Typography>
                    {items.map((item) => {
                      const showMenuButton =
                        Boolean(menuAnchorEl) && menuConversationId === item.uuid;
                      const isEditing = editingConversationId === item.uuid;
                      const isSelected = conversationId === item.uuid;
                      return (
                        <ListItemButton
                          key={item.uuid}
                          selected={isSelected}
                          onClick={(e) => {
                            if (!isEditing) {
                              handleSelectConversation(item.uuid, item.shortCode);
                            }
                          }}
                          sx={{
                            borderRadius: 2,
                            p: `3px 10px`,
                            minHeight: 42,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: isEditing ? "default" : "pointer",
                            "& .chat-menu-button": {
                              opacity: showMenuButton ? 1 : 0,
                            },
                            "&:hover .chat-menu-button": {
                              opacity: isEditing ? 0 : 1,
                            },
                            // Maintain selected background during editing
                            ...(isEditing && isSelected && {
                              backgroundColor: "action.selected",
                              "&:hover": {
                                backgroundColor: "action.selected",
                              },
                            }),
                          }}
                        >
                          {item.shared && (
                            <ShareIcon
                              aria-label="shared conversation"
                              sx={{ flexShrink: 0, color: "text.secondary", fontSize: 16, alignSelf: "center" }}
                            />
                          )}
                          {isEditing ? (
                            <TextField
                              inputRef={inputRef}
                              fullWidth
                              size="small"
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              onKeyDown={handleKeyDown}
                              onBlur={handleSaveRename}
                              onClick={(e) => e.stopPropagation()}
                              placeholder={t('sidebar.renamePlaceholder')}
                              variant="standard"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                my: 0,
                                '& .MuiInputBase-root': {
                                  fontSize: '0.875rem',
                                  lineHeight: 1.43,
                                  fontFamily: 'inherit',
                                  color: 'inherit',
                                  margin: 0,
                                  minHeight: 'unset',
                                  '&:before': {
                                    display: 'none',
                                  },
                                  '&:after': {
                                    display: 'none',
                                  },
                                  '&:hover:before': {
                                    display: 'none',
                                  },
                                },
                                '& .MuiInput-input': {
                                  padding: 0,
                                  cursor: 'text',
                                  lineHeight: 1.43,
                                  height: 'auto',
                                },
                              }}
                            />
                          ) : (
                            <ListItemText
                              sx={{ flex: 1, minWidth: 0 }}
                              primaryTypographyProps={{ variant: "body2", noWrap: true, component: "span" }}
                              secondaryTypographyProps={{ variant: "caption" }}
                              primary={
                                <>
                                  {props.streamingIds.has(item.uuid) && (
                                    <Box
                                      component="span"
                                      aria-label="generating"
                                      sx={{
                                        display: "inline-block",
                                        flexShrink: 0,
                                        mr: 0.75,
                                        ml: 0.5,
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: ramps.primary[500],
                                        boxShadow: `0 0 2px 0px ${hexToRGBA(ramps.primary[500], 0.5)}`,
                                        verticalAlign: "middle",
                                        position: "relative",
                                        top: "-1px",
                                        animation: "sentioStreamPulse 1.4s ease-in-out infinite",
                                        "@keyframes sentioStreamPulse": {
                                          "0%, 100%": { opacity: 0.55, transform: "scale(0.82)", boxShadow: `0 0 1px 0px ${hexToRGBA(ramps.primary[500], 0.35)}` },
                                          "50%": { opacity: 1, transform: "scale(1.12)", boxShadow: `0 0 2px 0.25px ${hexToRGBA(ramps.primary[500], 0.55)}` },
                                        },
                                      }}
                                    />
                                  )}
                                  {item.text || t('sidebar.conversation')}
                                </>
                              }
                              secondary={item.lastUpdated ? undefined : "No timestamp"}
                            />
                          )}
                          {!isEditing && !props.readOnly && !item.shared && (
                            <IconButton
                              className="chat-menu-button"
                              size="small"
                              onClick={(event) => handleMenuOpen(event, item.uuid)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItemButton>
                      );
                    })}
                  </Box>
                ))}
              </List>
              <Divider sx={{ flexShrink: 0, mt: "auto" }} />
              <ProfileSection />
            </S.StyledPaper>
          </S.StyledBoxPaperContainer>
        </Slide>
      </S.StyledGrid>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 160,
            maxWidth: 280,
            boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <MenuItem onClick={handleRename}>
          <EditIcon fontSize="small" sx={{ mr: 1, flexShrink: 0 }} />
          <Typography variant="body2" sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>{t('sidebar.rename')}</Typography>
        </MenuItem>
        <MenuItem onClick={handleArchive}>
          {props.conversations.find((item) => item.uuid === menuConversationId)?.status === "archived" ? (
            <>
              <UnarchiveOutlinedIcon fontSize="small" sx={{ mr: 1, flexShrink: 0 }} />
              <Typography variant="body2" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>{t('sidebar.unarchive')}</Typography>
            </>
          ) : (
            <>
              <ArchiveOutlinedIcon fontSize="small" sx={{ mr: 1, flexShrink: 0 }} />
              <Typography variant="body2" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>{t('sidebar.archive')}</Typography>
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteOutlineOutlinedIcon fontSize="small" sx={{ mr: 1, color: "error.main", flexShrink: 0 }} />
          <Typography variant="body2" sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>{t('sidebar.delete')}</Typography>
        </MenuItem>
      </Menu>
      <Dialog
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(""); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
            height: 500,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={t('sidebar.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, "& fieldset": { border: "none" } },
              }}
            />
            <IconButton
              size="small"
              onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
              aria-label="Close search"
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Divider />
        {!props.readOnly && (
          <Box sx={{ px: 1, py: 0.5 }}>
            <S.StyledButton
              variant="text"
              onClick={() => { handleNewConversation(); setSearchOpen(false); setSearchQuery(""); }}
              disableElevation
              fullWidth
              startIcon={<EditNoteIcon fontSize="large" />}
            >
              <Typography variant="body1">{t('sidebar.newChat')}</Typography>
            </S.StyledButton>
          </Box>
        )}
        <Divider />
        <List
          sx={{
            overflowY: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            p: 2,
            flex: 1,
            minHeight: 0,
          }}
        >
          {searchFilteredConversations.length === 0 && (
            <Typography variant="body2" color="text.secondary" p={3} align="center">
              {searchQuery ? t('sidebar.noResultsFound') : t('sidebar.noChatsYet')}
            </Typography>
          )}
          {Object.entries(searchGroupedConversations).map(([label, items]) => (
            <Box key={label} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 1, display: "block" }}>
                {label}
              </Typography>
              {items.map((item) => {
                const run = props.modelRuns?.find((r) => r.model_run_id === item.modelRunId);
                const runDate = run?.end_date ? new Date(run.end_date) : null;
                const runLabel = runDate && props.formatRunLabel
                  ? props.formatRunLabel(runDate, true)
                  : runDate
                  ? runDate.toLocaleString()
                  : null;
                const runIdShort = item.modelRunId ? item.modelRunId.replace(/-/g, "").slice(0, 8) : null;
                return (
                  <ListItemButton
                    key={item.uuid}
                    selected={conversationId === item.uuid}
                    onClick={() => {
                      handleSelectConversation(item.uuid, item.shortCode);
                      setSearchOpen(false);
                      setSearchQuery("");
                    }}
                    sx={{
                      borderRadius: 2,
                      p: `3px 10px`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {item.shared && (
                      <ShareIcon
                        aria-label="shared conversation"
                        sx={{ flexShrink: 0, color: "text.secondary", fontSize: 16, alignSelf: "center" }}
                      />
                    )}
                    <ListItemText
                      sx={{ flex: 1, minWidth: 0 }}
                      primaryTypographyProps={{ variant: "body2", noWrap: true, component: "span" }}
                      secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                      primary={item.text || t('sidebar.conversation')}
                      secondary={
                        (runLabel || runIdShort)
                          ? `${runLabel ?? ""}${runLabel && runIdShort ? "  ·  " : ""}${runIdShort ? `Run ${runIdShort}` : ""}`
                          : undefined
                      }
                    />
                  </ListItemButton>
                );
              })}
            </Box>
          ))}
        </List>
      </Dialog>
    </>
  )
}

export default React.memo(Sidebar);
